import { useCallback, useContext, useEffect, useState } from 'react';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import {
  associateWebAuthnCredential,
  listWebAuthnCredentials,
  deleteWebAuthnCredential,
} from 'aws-amplify/auth';
import { Card } from '../../design-system';
import { useConfirmAction } from '../ui/confirmactionhandler';
import SnackbarContext from '../../context/snackbar-context';
import { formatDate } from '../../utils/formatters';
import styles from './account.module.css';

/**
 * Passkeys section of the My Account page (Trello zaKPgqWh). Lists the user's
 * registered passkeys and lets them add one (browser WebAuthn ceremony) or remove
 * one. All calls are client-side Amplify -> Cognito; no backend route involved.
 */
export default function PasskeysCard() {
  const snackbar = useContext(SnackbarContext);
  const [credentials, setCredentials] = useState(null); // null = loading, [] = none
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { credentials: creds } = await listWebAuthnCredentials();
      setCredentials(creds || []);
    } catch (err) {
      setCredentials([]);
      snackbar.openSnackbarError(`Could not load passkeys: ${err?.message || err?.name || 'unknown error'}`);
    }
  }, [snackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    setAdding(true);
    try {
      await associateWebAuthnCredential();
      snackbar.openSnackbarSuccess('Passkey added.');
      await load();
    } catch (err) {
      // The browser throws when the user cancels or dismisses the prompt —
      // that is not an error worth surfacing.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        snackbar.openSnackbarError(`Could not add passkey: ${err?.message || err?.name || 'unknown error'}`);
      }
    } finally {
      setAdding(false);
    }
  };

  const doDelete = async (credential) => {
    try {
      await deleteWebAuthnCredential({ credentialId: credential.credentialId });
      snackbar.openSnackbarSuccess('Passkey removed.');
      await load();
    } catch (err) {
      snackbar.openSnackbarError(`Could not remove passkey: ${err?.message || err?.name || 'unknown error'}`);
    }
  };

  const { handleConfirmAction: confirmDelete, ConfirmDialogElement } = useConfirmAction(doDelete, {
    title: 'Remove passkey',
    getEntityName: (c) => c.friendlyCredentialName || 'this passkey',
    confirmMessage: 'You will no longer be able to sign in with this passkey.',
    proceedButtonLabel: 'Remove',
    destructive: true,
  });

  return (
    <Card padding="lg">
      <div className={styles.cardHead}>
        <FingerprintOutlinedIcon className={styles.cardIconAccent} aria-hidden="true" />
        <div className={styles.cardHeadText}>
          <div className={styles.cardTitle}>Passkeys</div>
          <div className={styles.cardDesc}>
            Sign in with Face ID, Touch ID, Windows Hello, or a security key.
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={handleAdd} disabled={adding}>
          {adding ? 'Waiting…' : 'Set up a passkey'}
        </button>
      </div>

      {credentials === null ? (
        <div className={styles.statusRow}>
          <span className={styles.muted}>Loading passkeys&hellip;</span>
        </div>
      ) : credentials.length === 0 ? (
        <div className={styles.emptyState}>
          <KeyOutlinedIcon className={styles.emptyIcon} aria-hidden="true" />
          <div>
            <div className={styles.emptyTitle}>No passkeys yet</div>
            <div className={styles.cardDesc}>Set up a passkey to sign in without a password.</div>
          </div>
        </div>
      ) : (
        <ul className={styles.rowList}>
          {credentials.map((c) => (
            <li key={c.credentialId} className={styles.row}>
              <KeyOutlinedIcon className={styles.rowIcon} aria-hidden="true" />
              <div className={styles.rowText}>
                <div className={styles.rowLabel}>{c.friendlyCredentialName || 'Passkey'}</div>
                <div className={styles.rowMeta}>
                  {c.createdAt ? `Added ${formatDate(c.createdAt)}` : 'Recently added'}
                </div>
              </div>
              <button
                className={styles.dangerBtn}
                onClick={() => confirmDelete(c)}
                aria-label={`Remove passkey ${c.friendlyCredentialName || ''}`.trim()}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialogElement />
    </Card>
  );
}
